import React from "react";
import Image from "next/image";
import { MeetPenTest } from "@/.storyblok/types/319751/storyblok-components";
import { renderRichText } from "@/lib/richText";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
// import { logger } from '@/lib/logger';

interface MeetPenTestProps {
  blok: MeetPenTest & SbBlokData;
}

const MeetPenTestComponent: React.FC<MeetPenTestProps> = ({ blok }) => {
  const { title, description, blog_author, pent_test_type, bg_style } = blok;

  if (!blog_author?.length) return null;
  // logger.log(`blog_author` + JSON.stringify(blog_author, null, 2));

  const backgroundClass = bg_style === "dark" ? "bg-light" : "";

  const swiperConfig = {
    modules: [Navigation, Pagination],
    spaceBetween: 10,
    slidesPerView: 1,
    navigation: {
      nextEl: ".swiper-button-next",
      prevEl: ".swiper-button-prev",
    },
    pagination: {
      el: ".swiper-pagination",
      clickable: true,
    },
  };

  if (pent_test_type === "red_bg") {
    return (
      <div {...storyblokEditable(blok)} className={`bg-primary bg-faded-edges`}>
        <div className="container row align-items-center py-4 py-md-5 mx-auto">
          {title && <h2 className="text-white text-center mb-3">{title}</h2>}
          {description && (
            <div
              className="lead text-center text-white max-two-thirds mb-5"
              dangerouslySetInnerHTML={{ __html: renderRichText(description) }}
            />
          )}
          <div className="swiper-carousel">
            <Swiper {...swiperConfig}>
              <ul className="list-unstyled cards-testimonials swiper-wrapper">
                {blog_author.map((author, index) => (
                  <SwiperSlide
                    key={typeof author === "string" ? index : (author.content?._uid ?? index)}
                    tag="li"
                    className="card card-lighter swiper-slide h-auto"
                  >
                    <div className="card-body">
                      <blockquote className="blockquote-red unstyled quote-red mb-0 ms-5">
                        {typeof author !== "string" && author.content?.meet_team}
                        <div className="row my-4 gx-4">
                          <div className="col-auto">
                            {typeof author !== "string" && author.content?.image?.filename && (
                              <Image
                                src={author.content.image.filename}
                                alt={`${author.name} Headshot`}
                                width={118}
                                height={118}
                                className="rounded-circle shadow js-lazy-load"
                                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                loading="lazy"
                                quality={75}
                              />
                            )}
                          </div>
                          <div className="col-auto">
                            <cite className="mt-0">
                              {typeof author !== "string" && (
                                <>
                                  <span className="fs-4 fw-bolder text-dark mb-0">{author.name}</span>
                                  <span
                                    className="d-block fs-4 fw-normal text-muted mb-1">{author.content?.title}</span>
                                  <span className="d-block fw-normal lead">
                  <i className="fas fa-fw fa-file me-2 text-secondary" aria-hidden="true"></i>
                  <a className="text-secondary" href={`/blog/author/${author.slug}`}>
                    See blogs by {author.name.split(" ")[0]}
                  </a>
                </span>
                                  {author.content?.linkedin?.url && (
                                    <span className="d-block fw-normal lead text-muted">
                    <i className="fab fa-fw fa-linkedin me-1 text-secondary" aria-hidden="true"></i>
                    <a
                      className="text-secondary"
                      href={author.content.linkedin.url}
                      target="_blank"
                      rel="noopener"
                    >
                      Follow {author.name.split(" ")[0]} on LinkedIn
                    </a>
                  </span>
                                  )}
                                </>
                              )}
                            </cite>
                          </div>
                          <div className="col"></div>
                        </div>
                      </blockquote>
                    </div>
                  </SwiperSlide>
                ))}
              </ul>
              <div className="swiper-pagination"></div>
            </Swiper>
            <div className="swiper-button-next"></div>
            <div className="swiper-button-prev"></div>
          </div>
        </div>
      </div>
    );
  }

  // Default layout
  return (
    <div {...storyblokEditable(blok)} key={blok._uid} className={`${backgroundClass}`}>
      <div className={`container`}>
        <section className="py-4 py-md-5">
          {title && <h2 className="text-center">{title}</h2>}
          {description && (
            <div
              className="lead text-center mb-4"
              dangerouslySetInnerHTML={{ __html: renderRichText(description) }}
            />
          )}

          {blog_author.length === 1 && typeof blog_author[0] !== "string" ? (
            // Single author layout
            <div className="carousel-testimonial col-16 text-center mx-auto">
              {blog_author[0]?.content?.image?.filename && (
                <Image
                  src={blog_author[0].content.image.filename}
                  alt={`${blog_author[0].name} Headshot`}
                  width={225}
                  height={225}
                  className="rounded-circle shadow js-lazy-load"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  loading="lazy"
                  quality={75}
                />
              )}
              <blockquote className="unstyled text-center quote-red mb-3 mb-md-4">
                {blog_author[0].content?.meet_team}
                <cite>
        <span className="lead fw-bold text-dark mb-0">
          {blog_author[0].name?.split(" ")[0]}
        </span>
                  <span className="d-block fw-normal lead text-muted mb-1">
          Bulletproof {blog_author[0].content?.title}
        </span>
                  <span className="d-block fw-normal lead">
          <i className="fas fa-fw fa-file me-2 text-secondary" aria-hidden="true"></i>
          <a className="text-secondary" href={`/blog/author/${blog_author[0].slug}`}>
            See blogs by {blog_author[0].name?.split(" ")[0]}
          </a>
        </span>
                  {blog_author[0]?.content?.linkedin?.url && (
                    <span className="d-block fw-normal lead text-muted">
            <i className="fab fa-fw fa-linkedin me-1 text-secondary" aria-hidden="true"></i>
            <a
              className="text-secondary"
              href={blog_author[0].content.linkedin.url}
              target="_blank"
              rel="noopener"
            >
              Follow {blog_author[0].name?.split(" ")[0]} on LinkedIn
            </a>
          </span>
                  )}
                </cite>
              </blockquote>
            </div>
          ) : (
            // Multiple authors layout with slider
            <div className="swiper-carousel">
              <Swiper {...swiperConfig}>
                <ul className="list-unstyled cards-testimonials swiper-wrapper">
                  {blog_author.map((author, index) => (
                    typeof author !== "string" && (
                      <SwiperSlide
                        key={author.content?._uid ?? index}
                        tag="li"
                        className="card card-lighter swiper-slide h-auto"
                      >
                        <div className="card-body">
                          <blockquote className="blockquote-red unstyled quote-red mb-0 ms-5">
                            {author.content?.meet_team}
                            <div className="row my-4 gx-4">
                              <div className="col-auto">
                                {author?.content?.image?.filename && (
                                  <Image
                                    src={author.content.image.filename}
                                    alt={`${author.name} Headshot`}
                                    width={118}
                                    height={118}
                                    className="rounded-circle shadow js-lazy-load"
                                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                    loading="lazy"
                                    quality={75}
                                  />
                                )}
                              </div>
                              <div className="col-auto">
                                <cite className="mt-0">
                                  <span className="fs-4 fw-bolder text-dark mb-0">{author.name}</span>
                                  <span
                                    className="d-block fs-4 fw-normal text-muted mb-1">{author.content?.title}</span>
                                  <span className="d-block fw-normal lead">
                          <i className="fas fa-fw fa-file me-2 text-secondary" aria-hidden="true"></i>
                          <a className="text-secondary" href={`/blog/author/${author.slug}`}>
                            See blogs by {author.name?.split(" ")[0]}
                          </a>
                        </span>
                                  {author?.content?.linkedin?.url && (
                                    <span className="d-block fw-normal lead text-muted">
                            <i className="fab fa-fw fa-linkedin me-1 text-secondary" aria-hidden="true"></i>
                            <a
                              className="text-secondary"
                              href={author.content.linkedin.url}
                              target="_blank"
                              rel="noopener"
                            >
                              Follow {author.name?.split(" ")[0]} on LinkedIn
                            </a>
                          </span>
                                  )}
                                </cite>
                              </div>
                              <div className="col"></div>
                            </div>
                          </blockquote>
                        </div>
                      </SwiperSlide>
                    )
                  ))}
                </ul>
                <div className="swiper-pagination"></div>
              </Swiper>
              <div className="swiper-button-next"></div>
              <div className="swiper-button-prev"></div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default MeetPenTestComponent;